import type { Ref } from 'react';

import { useIntersectionObserver } from 'usehooks-ts';

import { trackingUtils } from '@debijenkorf/dbk-common';

import {
  selectItemEvent,
  viewItemListEvent,
  type GA4ProductItemInput,
  type MapListProductToGA4ItemArgs,
} from '@/utilities/tracking/ga4ProductModel';

export type AnalyticsEvent<Product> = {
  product?: Product;
  position?: number;
};

type AnalyticsData<Product> = {
  location: string;
  listId?: string;
  listName?: string;
  mapProductToGA4Item: (args: MapListProductToGA4ItemArgs<Product>) => GA4ProductItemInput;
};

type UseListTrackingArgs<Product> = {
  products: Product[];
  analyticsData: AnalyticsData<Product>;
};

export type UseListTrackingReturn<Product> = {
  ref: Ref<HTMLDivElement>;
  handleAnalyticsEvent: (analyticsEvent: AnalyticsEvent<Product>) => void;
};

export const useListTracking = <Product>({
  products,
  analyticsData,
}: UseListTrackingArgs<Product>): UseListTrackingReturn<Product> => {
  const getGA4Item = (position: number) =>
    analyticsData.mapProductToGA4Item({
      product: products[position - 1],
      locationId: analyticsData.location,
      index: position,
      itemListId: analyticsData.listId,
      itemListName: analyticsData.listName,
    });

  const { ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    onChange: isIntersecting => {
      if (!isIntersecting || !products.length) return;

      const items = products.map((_, index) => getGA4Item(index + 1));

      trackingUtils.pushToDataLayer(viewItemListEvent(items));
    },
  });

  const handleAnalyticsEvent = ({ position = 0 }: AnalyticsEvent<Product>) => {
    if (!products[position - 1]) return;

    trackingUtils.pushToDataLayer({ ecommerce_ga4: null });
    trackingUtils.pushToDataLayer(selectItemEvent(getGA4Item(position)));
  };

  return {
    ref,
    handleAnalyticsEvent,
  };
};
